import React from 'react'
import { createRoot } from 'react-dom/client'

import { ImportTracker } from 'components/importTracker'
import { loadUniques } from 'data/load'
import Store, { EmbedStore } from 'store'
import EditingContext from 'store/editingContext'
import Session from 'store/session'
import OverlayRoot from 'ui/utils/OverlayManager'

import { Api } from 'api'
import {
  PassiveTreeEmbed,
  SelectAscendancy,
  SelectClass,
  SelectVersion,
  useChangeVersion
} from 'components/embeds/Passives'

import {
  faCloudArrowDown,
  faCompressArrowsAlt,
  faEdit,
  faExpandArrowsAlt,
  faSave
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ProfilesSection from 'components/import/Profiles'
import SaveSection from 'components/import/Save'
import Data from 'data'
import Dialog from 'ui/dialog'
import { DndProviderWrapper } from 'ui/dragdrop'
import './TreeApp.scss'

import { CharacterLoaderDialog } from '@/components/planner/header/character-loader'
import { useLoadCharacters } from '@/components/planner/header/character-loader/api'
import { validateTreeEmbed } from '@/logic/tree'
import { validateState } from '@/logic/validation'
import { TreeHandle } from '@/tree/TreeViewer'
import { ProfileType } from '@/types/api'
import { Embed } from '@/types/embed'
import { PlayerClass } from '@/types/profile'
import { versionNumberToString } from '@/utils'
import { NotificationsProvider, SearchInput } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import { BuildInfoLevel, Game, getApiPath } from 'common'
import WithTreeData from 'components/WithTreeData'
import 'components/import/ImportPane.scss'

function ProfileDialog({
  onPreview,
  onClose,
  type
}: {
  onPreview: () => void
  onClose: () => void
  type: ProfileType
}) {
  const loggedIn = Session.useSelector((store) => store.user != null)

  return (
    <Dialog className="poe-TreeProfileDialog" title="Settings" onClose={onClose}>
      <p>
        <span onClick={onPreview}>
          <span className="poe-toggle poe-active" /> Editing Mode
        </span>
      </p>
      {!loggedIn && <div>Log in using your Maxroll account to track your saved profiles</div>}
      <div className="poe-TabContent poe-ImportPane">
        <div className="poe-Section poe-expanded poe-always">
          <h3>Save</h3>
          <SaveSection type={type} />
        </div>
        {!!loggedIn && (
          <div className="poe-Section poe-expanded poe-always">
            <h3>My Profiles</h3>
            <ProfilesSection type={type} />
          </div>
        )}
      </div>
    </Dialog>
  )
}

function useContentCreator() {
  return Session.useSelector((state) => (state.user?.active ?? 0) >= BuildInfoLevel)
}

function TreeApp({ loading }: { loading: boolean }) {
  const [editing, setEditing] = React.useState(!loading)
  const [showUi, setShowUi] = React.useState(false)
  const [fullscreen, setFullscreen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const items = Store.useSelector((state) => state.items)
  const profileId = Store.useSelector((state) => state.imported?.id ?? `${state.id}`)

  const isCC = useContentCreator()

  const state = Store.useSelector((state) => state.passive_tree ?? EmbedStore.passives)
  const dispatch = Store.useDispatch()
  const onChange = React.useCallback(
    (value: Embed | ((prev: Embed) => Embed)) =>
      dispatch.setState((state) => {
        const prev = state.passive_tree || EmbedStore.passives
        if (typeof value === 'function') value = value(prev)
        if (value === prev) return state
        if (value.type !== 'passives' && value.type !== 'atlas') return state
        return { ...state, passive_tree: value }
      }),
    [dispatch]
  )

  const onChangeClass = React.useCallback(
    (value: PlayerClass) =>
      dispatch.setState((state) => {
        const tree = state.passive_tree ?? EmbedStore.passives
        if (tree.type === 'atlas' || tree.charClass === value) return state
        state = { ...state }
        state.passive_tree = { ...tree }
        state.passive_tree.charClass = value
        state.passive_tree.ascendancy = undefined
        state.passive_tree.variants = EmbedStore.passives.variants
        state.passive_tree.active = 0
        return state
      }),
    [dispatch]
  )
  const onChangeAscendancy = React.useCallback(
    (value: string | undefined) =>
      dispatch.setState((state) => {
        const tree = state.passive_tree ?? EmbedStore.passives
        if (tree.type === 'atlas' || tree.ascendancy === value) return state
        state = { ...state }
        state.passive_tree = validateTreeEmbed(
          {
            ...tree,
            ascendancy: value
          },
          items
        )
        return state
      }),
    [dispatch, items]
  )

  const onShowUi = React.useCallback(() => setShowUi(true), [])
  const onHideUi = React.useCallback(() => setShowUi(false), [])
  const onEdit = React.useCallback(() => setEditing(true), [])
  const onPreview = React.useCallback(() => {
    setShowUi(false)
    setEditing(false)
  }, [])

  const className = React.useMemo(() => {
    if (state.type === 'atlas') return undefined
    const chr = Data.characters[state.charClass]
    if (!chr) return undefined
    const asc = state.ascendancy && chr.ascendancies[state.ascendancy]
    if (!asc) return chr.name
    return `${chr.name} - ${asc.name}`
  }, [state])

  const treeRef = React.useRef<TreeHandle>(null)
  const onSearchKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') treeRef.current?.focus({ search })
    },
    [search]
  )
  const onChangeVersion = useChangeVersion(onChange)

  const { showCharImportModal, setShowCharImportModal, loadCharacters } = useLoadCharacters()

  return (
    <div className={classNames('poe-TreeApp', { 'poe-fullscreen': fullscreen })}>
      <WithTreeData version={state.version}>
        <PassiveTreeEmbed
          embed={state}
          onChange={editing ? onChange : undefined}
          limited
          showNotes={isCC}
          search={search}
          profileId={profileId}
          ref={treeRef}>
          <div className="poe-tree-app-buttons">
            <div
              className={classNames('poe-tree-fullscreen', {
                'poe-active': fullscreen
              })}
              onClick={() => setFullscreen((f) => !f)}>
              <FontAwesomeIcon icon={fullscreen ? faCompressArrowsAlt : faExpandArrowsAlt} />
            </div>
            <div
              className={classNames('poe-save-button', {
                'poe-active': editing
              })}
              onClick={editing ? onShowUi : onEdit}>
              <FontAwesomeIcon icon={editing ? faSave : faEdit} />
            </div>
            <div className="poe-import-button" onClick={loadCharacters}>
              <FontAwesomeIcon icon={faCloudArrowDown} />
            </div>
          </div>
          <div className="poe-tree-controls-wrap">
            <div className="poe-tree-controls">
              {editing ? (
                <SelectVersion version={state.version} onChange={onChangeVersion} />
              ) : (
                <div className="poe-version">Patch {versionNumberToString(state.version)}</div>
              )}
              {state.type !== 'atlas' &&
                (editing ? (
                  <>
                    <SelectClass value={state.charClass} onChange={onChangeClass} />
                    <SelectAscendancy
                      charClass={state.charClass}
                      value={state.ascendancy}
                      onChange={onChangeAscendancy}
                      allowEmpty
                      version={state.version}
                    />
                  </>
                ) : (
                  <div className="poe-class">{className}</div>
                ))}
              <div className="poe-search-new">
                <SearchInput
                  size="sm"
                  collapsible={true}
                  value={search}
                  onChange={setSearch}
                  onKeyDown={onSearchKeyDown}
                  placeholder="Search"
                  innerClass="poe-search-new-input"
                />
              </div>
            </div>
          </div>
        </PassiveTreeEmbed>
      </WithTreeData>
      {!!showUi && (
        <ProfileDialog
          onPreview={onPreview}
          onClose={onHideUi}
          type={state.type === 'atlas' ? 'atlas-tree' : 'passive-tree'}
        />
      )}
      <ImportTracker type={state.type === 'atlas' ? 'atlas-tree' : 'passive-tree'} />
      {showCharImportModal && (
        <CharacterLoaderDialog
          type={state.type === 'atlas' ? 'atlas' : 'passives'}
          onClose={() => setShowCharImportModal(false)}
        />
      )}
    </div>
  )
}

document.addEventListener('DOMContentLoaded', async () => {
  const root = document.querySelector('.poeplanner-passive-tree, .poeplanner-atlas-tree')
  if (!root) return

  const atlas = root.classList.contains('poeplanner-atlas-tree')

  const session = new Session()
  const api = new Api(session.dispatch)

  const store = new Store({
    passive_tree: atlas ? EmbedStore.atlas : EmbedStore.passives
  })

  const dataLoaded = loadUniques()

  let loading = null
  const match = window.location.pathname.match(/\/(?:poe-passive-tree|poe-atlas-tree)\/(\w{8,9})$/)
  if (match) {
    const url = getApiPath(`profiles/load/${Game}/${match[1]}`)
    loading = fetch(url)
      .then((response) => response.json())
      .then(
        async (result) => {
          await dataLoaded
          const { data, ...imported } = result
          if (!data) {
            if (result.error === 'Profile not found') {
              console.warn('Profile not found')
            }

            return
          }
          const state = await validateState(JSON.parse(data), imported)
          if (!state.passive_tree) return
          await store.setState(state)
        },
        (error) => error.message
      )
  }

  await dataLoaded

  if (loading) {
    await loading
  } else {
    await store.setState((state) => {
      state = { ...state }
      state.passive_tree = {
        ...(atlas ? EmbedStore.atlas : EmbedStore.passives),
        version: Math.max(...Data.versions)
      }
      return state
    })
  }

  const editingContext = new EditingContext()

  api.updateLoginFrontend()

  root.classList.add('poeplanner-wrapper')
  createRoot(root).render(
    <NotificationsProvider>
      <DndProviderWrapper>
        <Store.Provider store={store}>
          <Session.Provider store={session}>
            <EditingContext.Provider context={editingContext}>
              <OverlayRoot>
                <TreeApp loading={!!loading} />
              </OverlayRoot>
            </EditingContext.Provider>
          </Session.Provider>
        </Store.Provider>
      </DndProviderWrapper>
    </NotificationsProvider>
  )
})
